import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { Layout } from "@/components/Layout";
import { IncidentDetail } from "@/components/IncidentDetail";
import { CallInBanner } from "@/components/CallInBanner";
import { DashboardToolbar } from "@/components/dashboard/DashboardToolbar";
import { IncidentList } from "@/components/dashboard/IncidentList";
import { EmptyDetailState } from "@/components/dashboard/EmptyDetailState";
import { toast } from "sonner";

export default function Dashboard() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [sevFilter, setSevFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/incidents");
      setIncidents(data);
      setSelectedId((cur) => cur || (data.length ? data[0].id : cur));
    } catch (err) {
      logError("Failed to load incidents", err);
      toast.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return incidents.filter((i) => {
      const matchQ = !query ||
        `${i.title} ${i.location} ${i.description} ${i.caller_number}`.toLowerCase().includes(query.toLowerCase());
      const matchS = sevFilter === "all" || i.severity === sevFilter;
      return matchQ && matchS;
    });
  }, [incidents, query, sevFilter]);

  const selected = incidents.find((i) => i.id === selectedId);

  const onUpdated = (updated) => {
    setIncidents((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };
  const onDeleted = (id) => {
    setIncidents((prev) => prev.filter((i) => i.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
  };

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        <CallInBanner />
        <div className="flex flex-1 overflow-hidden min-h-0">
          <aside className="w-full md:w-[34%] lg:w-[30%] border-r border-border flex flex-col">
            <DashboardToolbar count={incidents.length} query={query} setQuery={setQuery}
              sevFilter={sevFilter} setSevFilter={setSevFilter} />
            <IncidentList loading={loading} incidents={filtered} selectedId={selectedId} onSelect={setSelectedId} />
          </aside>
          <section className="hidden md:flex flex-1 flex-col overflow-hidden">
            {selected ? (
              <IncidentDetail incident={selected} onUpdated={onUpdated} onDeleted={onDeleted} />
            ) : (
              <EmptyDetailState />
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}
